import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { APP_ROUTES } from '@core/constants/app-routes.const';
import { AuthService } from '@core/services/auth.service';
import { LanguageService } from '@core/services/language.service';
import { ThemeMode, ThemeService } from '@core/services/theme.service';
import { UserService } from '@core/services/user.service';
import { getUserInitials } from '@core/utils/user-initials.util';
import {
  UserOption,
  UserOptionsMenuComponent,
} from '@shared/components/user-options-menu/user-options-menu.component';

interface ThemeOption {
  value: ThemeMode;
  icon: string;
  labelKey: string;
}

@Component({
  selector: 'app-user-menu',
  standalone: true,
  imports: [UserOptionsMenuComponent, TranslocoModule],
  templateUrl: './user-menu.component.html',
  styleUrl: './user-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserMenuComponent {
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly transloco = inject(TranslocoService);

  readonly closeEvent = output<void>();

  readonly isLanguageMenuOpen = signal(false);
  readonly currentLanguage = this.languageService.currentLanguage;
  readonly themeMode = this.themeService.mode;

  readonly languageOptions: UserOption[] = [
    { label: 'Español', value: 'es' },
    { label: 'English', value: 'en' },
  ];

  readonly themeOptions: ThemeOption[] = [
    { value: 'light', icon: 'light_mode', labelKey: 'USER_MENU.THEME_LIGHT' },
    { value: 'dark', icon: 'dark_mode', labelKey: 'USER_MENU.THEME_DARK' },
    {
      value: 'system',
      icon: 'desktop_windows',
      labelKey: 'USER_MENU.THEME_SYSTEM',
    },
  ];

  // El idioma activo entra como dependencia para que el texto de respaldo
  // ("Usuario") se retraduzca al cambiar de idioma.
  private readonly activeLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  private readonly userData = computed(() => {
    this.userService.userName();
    return this.userService.getUserData();
  });

  readonly fullName = computed(() => {
    this.activeLang();
    return (
      this.userData()?.fullName ??
      this.userService.userName() ??
      this.transloco.translate('NAVBAR.USER')
    );
  });

  readonly email = computed(
    () => this.userData()?.email ?? this.userService.userName() ?? '',
  );

  readonly cargo = computed(() => this.userData()?.mainRole ?? '');

  readonly initials = computed(() => getUserInitials(this.fullName()));

  readonly currentLanguageName = computed(
    () =>
      this.languageOptions.find((l) => l.value === this.currentLanguage())
        ?.label ?? 'Español',
  );

  toggleLanguageMenu(): void {
    this.isLanguageMenuOpen.update((open) => !open);
  }

  /** El panel se ocultó (Esc, clic fuera o una acción): al reabrir no debe seguir el submenú. */
  closeLanguageMenu(): void {
    this.isLanguageMenuOpen.set(false);
  }

  onLanguageSelected(value: string): void {
    this.languageService.changeLanguage(value);
    this.isLanguageMenuOpen.set(false);
  }

  setTheme(mode: ThemeMode): void {
    this.themeService.setMode(mode);
  }

  openChangePassword(): void {
    this.closeEvent.emit();
    this.router.navigate([APP_ROUTES.HERRAMIENTAS.CAMBIAR_CONTRASENA]);
  }

  logout(): void {
    this.closeEvent.emit();
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
